import {Image, Link} from '@shopify/hydrogen';
import {Product} from '@shopify/hydrogen/storefront-api-types';
import clsx from 'clsx';
import {FC, ReactElement} from 'react';
import type {PartialDeep} from 'type-fest';

import {ProductReview} from '~/components';
import {formatNumber} from '~/utils';
import {useMobile} from '~/hooks';

const ProductSubscriptionPrice: FC<{
  product?: PartialDeep<Product>;
}> = ({product}) => {
  const gbbSellingPlan = product?.sellingPlanGroups?.edges?.find(
    (item) => item?.node?.name === 'GBB' || item?.node?.name === 'GBB 10%',
  )?.node;

  if (!gbbSellingPlan || !Array.isArray(gbbSellingPlan.sellingPlans?.nodes))
    return null;

  const plans = gbbSellingPlan.sellingPlans?.nodes;

  const maxValue = plans.reduce((prev, cur) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line no-unsafe-optional-chaining
    const {adjustmentPercentage} = cur?.priceAdjustments[0]?.adjustmentValue;
    return prev < adjustmentPercentage ? adjustmentPercentage : prev;
  }, 0);

  return (
    <div className="rounded-full bg-[#E9E3DC] px-[12px] py-[3px] text-sm font-semibold text-[#FA685B]">
      Save up to {maxValue}%
    </div>
  );
};

export function ServiceItem({
  product,
  isMobileMode = false,
  className,
}: {
  product?: PartialDeep<Product>;
  className?: string;
  isMobileMode?: boolean;
}): ReactElement {
  const productTitleArr = (product?.title ?? 'Product Title').split(' ');

  const hoverNode = product?.images?.nodes?.[1];
  const baseNode = product?.images?.nodes?.[0];
  const productTime = product?.productType?.split('|')[0]?.trim() ?? '';
  const productType = product?.productType?.split('|')[1]?.trim() ?? '';

  const isMobile = useMobile();

  const renderMobile = () => {
    return (
      <div className="mt-2 flex gap-3 border-b border-solid border-b-[#e9e3dc] pb-4 last:border-b-0 last:pb-0">
        <div
          className={clsx('relative overflow-hidden text-[#4B2304]', className)}
        >
          <Link to={`/products/${product?.handle}`}>
            <Image
              width={120}
              height={120}
              src={baseNode?.url ?? 'https://none'}
              alt={baseNode?.altText ?? 'Service Image'}
              className="aspect-[0.88] rounded-lg object-cover"
            />
            {product?.tags?.find((tag) => tag === 'Popular') && (
              <div className="absolute left-[10px] top-[10px] rounded-[30px] bg-white p-[6px] text-[11px] font-bold leading-none text-acai-900">
                Popular
              </div>
            )}
          </Link>
        </div>

        <div className="flex flex-col">
          <div className="service-Item-text-box flex items-center justify-between">
            <Link to={`/products/${product?.handle}`}>
              <div className="text-[20px] leading-5 text-[#4B2304]">
                {productTitleArr[0]}{' '}
                {productTitleArr[1] == 'Glow' && productTitleArr[1]}
                {productTitleArr[1] == 'a' && productTitleArr[1]}
                {productTitleArr.length > 1 && (
                  <>
                    {' '}
                    <span className="font-serif italic">
                      {productTitleArr
                        .filter(
                          (str, index) =>
                            index !== 0 && str !== 'Glow' && str !== 'a',
                        )
                        .join(' ')}
                    </span>
                  </>
                )}
              </div>
            </Link>
          </div>
          <div className="mt-[8px] text-[12px] font-bold leading-3 text-[#593B37]">
            {productType}
          </div>
          <div className="mt-[8px] flex-grow text-[11px] leading-[11px] text-[#70655C]">
            {productTime}
          </div>
          <div className="text-[11px]">
            <ProductReview product={product as Product} />
          </div>
          <div className="mt-[8px] flex items-center justify-between gap-2">
            <div className="text-[12px] font-bold text-[#4B2304]">
              {`${formatNumber(
                product?.variants?.nodes?.at(0)?.priceV2?.amount as string,
              )} ${product?.variants?.nodes?.at(0)?.priceV2?.currencyCode}`}
            </div>
            <ProductSubscriptionPrice product={product} />
          </div>
        </div>
      </div>
    );
  };

  const renderDesktop = () => {
    return (
      <Link to={`/products/${product?.handle}`}>
        <div
          className={clsx('relative overflow-hidden text-[#4B2304]', className)}
        >
          <>
            <Image
              width={baseNode?.width ?? 1}
              height={baseNode?.height ?? 1}
              src={baseNode?.url ?? 'https://none'}
              alt={baseNode?.altText ?? 'Service Image'}
              className="aspect-[6/7] rounded-lg object-cover transition-all duration-100 hover:saturate-150"
            />
            {hoverNode?.url && (
              <Image
                width={hoverNode?.width ?? 1}
                height={hoverNode?.height ?? 1}
                src={hoverNode?.url ?? 'https://none'}
                alt={hoverNode?.altText ?? 'Service Image'}
                className="width-full absolute left-0 top-0 aspect-[6/7] rounded-lg object-cover opacity-0 transition-opacity duration-100 hover:opacity-100"
              />
            )}
          </>
          {product?.tags?.find((tag) => tag === 'Popular') && (
            <div className="absolute left-[10px] top-[10px] rounded-[30px] bg-white p-[6px] text-[11px] font-bold leading-none text-acai-900">
              Popular
            </div>
          )}
        </div>
        <div className="mt-2 flex flex-col">
          <div className="service-Item-text-box flex items-center justify-between">
            <div className="text-base text-[#4B2304]">
              {productTitleArr[0]}{' '}
              {productTitleArr[1] == 'Glow' && productTitleArr[1]}
              {productTitleArr[1] == 'a' && productTitleArr[1]}
              {productTitleArr.length > 1 && (
                <>
                  {' '}
                  <span className="font-serif italic">
                    {productTitleArr
                      .filter(
                        (str, index) =>
                          index !== 0 && str !== 'Glow' && str !== 'a',
                      )
                      .join(' ')}
                  </span>
                </>
              )}
            </div>
            <ProductReview product={product as Product} />
          </div>
          <div className="text-sm text-[#70655C]">{product?.productType}</div>
          <div className="mt-[4px] flex justify-between">
            <div className="text-sm font-bold text-acai-900">
              {`${formatNumber(
                product?.variants?.nodes?.at(0)?.priceV2?.amount as string,
              )} ${product?.variants?.nodes?.at(0)?.priceV2?.currencyCode}`}
            </div>
            <ProductSubscriptionPrice product={product} />
          </div>
        </div>
      </Link>
    );
  };

  return isMobileMode && isMobile ? renderMobile() : renderDesktop();
}
